import { useMemo, useCallback, useEffect, useState, useRef } from 'react'

import { useLazyQuery } from '@apollo/client'
import type { Schema } from '@rocicorp/zero/react'
import { useZero, useQuery as useZeroQuery } from '@rocicorp/zero/react'
import type { ObjectPropertyDefinition } from 'types/graphql'

import { useAuth } from 'src/auth'
import { logger } from 'src/lib/logger'
import {
  buildObjectReference,
  NativeObjectTypes,
  ObjectTypeMetadata,
  type NativeObjectType,
} from 'src/lib/objects'
import {
  StandardProperties,
  StandardPropertyAsObjectPropertyDefinition,
} from 'src/lib/Properties/properties'

import ObjectsContext from './ObjectsContext'
import type { SyncState } from './ObjectsContext'
import {
  GET_ALL_OBJECTS_UPDATED_AT,
  GET_WORKSPACE_OBJECTS_BY_IDS,
} from './queries'
import {
  upsertDayObjects,
  getDayObjectStateByType,
  buildFuseIndex,
  addToSyncQueue,
  getDayObjectsByType,
  deleteDayObjectSearchEntry,
  removeFromSyncQueue,
} from './searchIndex'
import useSyncQueueProcessor from './useSyncQueueProcessor'

const loggingEnabled = true || !process.env.HOST.includes('localhost')

const initialSyncState: SyncState = {
  ...Object.fromEntries(
    Object.values(NativeObjectTypes)
      .filter((objectType) => ObjectTypeMetadata[objectType]?.syncEnabled)
      .map((objectType) => [
        objectType,
        {
          objectType,
          objectsNeedingSync: 0,
          currentCount: 0,
          updatedAt: null,
          syncedAt: null,
          processedUntilTimestamp: null,
        },
      ])
  ),
}

const ObjectsProvider = ({
  workspaceId,
  children,
}: {
  workspaceId: string
  children: React.ReactNode
}) => {
  const { currentUser } = useAuth()

  // Use refs for stable values that don't need to trigger re-renders
  const workspaceIdRef = useRef(workspaceId)
  const userEmailRef = useRef(currentUser?.email)

  // Update refs when values change
  useEffect(() => {
    workspaceIdRef.current = workspaceId
  }, [workspaceId])

  useEffect(() => {
    userEmailRef.current = currentUser?.email
  }, [currentUser?.email])

  const [syncState, setSyncState] = useState<SyncState | null>(initialSyncState)

  const [getWorkspaceObjectsByIds] = useLazyQuery(GET_WORKSPACE_OBJECTS_BY_IDS)

  // Stable logInfo with no dependencies
  const logInfo = useCallback(
    (message: string, data: any, level: 'info' | 'warn' = 'info') => {
      if (loggingEnabled) {
        logger[level]({
          ...data,
          message,
          component: 'ObjectsProvider',
          workspaceId: workspaceIdRef.current,
          userEmail: userEmailRef.current,
        })
      }
    },
    [] // No dependencies - uses refs instead
  )

  const renderCount = useRef(0)
  renderCount.current += 1
  logInfo('ObjectsProvider render', {
    renderCount: renderCount.current,
    workspaceId,
  })

  const handleResyncObjects = useCallback(
    async (objectType: NativeObjectType, objectIds: string[]) => {
      logInfo('handleResyncObjects', {
        message: 'Resyncing objects',
        objectIdsCount: objectIds?.length,
        objectType,
      })

      try {
        const result = await getWorkspaceObjectsByIds({
          variables: {
            workspaceId,
            objectType,
            objectIds,
          },
        })

        const objects = result?.data?.workspaceObjectsByIds || []

        // Log if we didn't get all requested objects
        if (objects.length !== objectIds.length) {
          const returnedIds = objects.map((obj) => obj.objectId)
          const missingIds = objectIds.filter((id) => !returnedIds.includes(id))

          logInfo('handleResyncObjects: Missing objects from query', {
            objectType,
            requestedCount: objectIds.length,
            returnedCount: objects.length,
            missingCount: missingIds.length,
            missingIds: missingIds.slice(0, 10), // First 10 for logging
          })
        }

        // Process the objects we did get
        if (objects.length > 0) {
          await upsertDayObjects({
            objects,
            objectType,
            workspaceId,
            synced: true,
          })

          await buildFuseIndex(workspaceId)
        }

        // CRITICAL: Always remove items from sync queue after processing
        // This prevents infinite retry loops
        await removeFromSyncQueue({
          workspaceId,
          objectType,
          objectIds,
        })

        logInfo('handleResyncObjects: Removed items from sync queue', {
          objectType,
          removedCount: objectIds.length,
        })
      } catch (error) {
        logInfo(
          'handleResyncObjects: Error processing objects',
          {
            error: error.message,
            objectType,
            objectIdsCount: objectIds.length,
          },
          'warn'
        )

        // Remove items from queue even on error to prevent infinite retry
        // This is especially important for authorization errors
        await removeFromSyncQueue({
          workspaceId,
          objectType,
          objectIds,
        })

        // Re-throw the error so it can be handled upstream if needed
        throw error
      }
    },
    [workspaceId, getWorkspaceObjectsByIds, logInfo]
  )

  useSyncQueueProcessor({
    workspaceId,
    processItems: handleResyncObjects,
    filterItems: (_, objectIds) => objectIds,
    batchSize: 100,
    pollingInterval: { active: 100, idle: 5000 },
  })

  const z = useZero<Schema>()

  const [propertyDefinitionsResultsZero, result] = useZeroQuery(
    z.query.objectPropertyDefinition
      .where('workspaceId', workspaceId)
      .related('objectPropertyDefinitionOptions'),
    {
      enabled: !!workspaceId,
    }
  )

  const propertyDefinitions = useMemo(() => {
    if (!result) {
      // Ensure a valid structure is always returned to satisfy ObjectsContextType
      const initialOutput: Record<
        NativeObjectType,
        Record<string, ObjectPropertyDefinition>
      > = {} as any
      for (const type of Object.values(NativeObjectTypes)) {
        if (typeof type === 'string') {
          initialOutput[type as NativeObjectType] = {}
        }
      }
      return initialOutput
    }

    const output: Record<
      NativeObjectType,
      Record<string, ObjectPropertyDefinition>
    > = {} as any
    // Initialize all native object types
    for (const type of Object.values(NativeObjectTypes)) {
      if (typeof type === 'string') {
        // Ensure type is a string key from the enum/object
        output[type as NativeObjectType] = {}
      }
    }

    for (const objectType of Object.keys(StandardProperties)) {
      if (!output[objectType as NativeObjectType]) {
        output[objectType as NativeObjectType] = {}
      }
      for (const propId of Object.keys(StandardProperties[objectType])) {
        output[objectType as NativeObjectType][propId] =
          StandardPropertyAsObjectPropertyDefinition({
            objectType: objectType as NativeObjectType,
            propertyId: propId as string,
            workspaceId,
          })
      }
    }

    const propDefsRaw =
      propertyDefinitionsResultsZero as unknown as ObjectPropertyDefinition[]

    for (const propDef of propDefsRaw) {
      if (propDef) {
        const objectType = propDef.objectTypeId as NativeObjectType
        if (!output[objectType]) {
          output[objectType] = {}
        }
        // Linter fix: Use 'as any' if GQLObjectPropertyDefinition doesn't have objectPropertyDefinitionOptions
        // This suggests the GQLObjectPropertyDefinition type might be out of sync with the actual data structure.
        const options = (propDef as any).objectPropertyDefinitionOptions
        const entry = {
          ...propDef,
          options: options,
        }
        // If GQLObjectPropertyDefinition type is correct, this delete is not needed
        // and 'options' should already be part of the spread propDef or handled by the type.
        // delete (entry as any).objectPropertyDefinitionOptions;
        output[objectType][propDef.id] = entry
      }
    }
    return output
  }, [result, propertyDefinitionsResultsZero, workspaceId])

  const [getAllObjectsUpdatedAt] = useLazyQuery(GET_ALL_OBJECTS_UPDATED_AT)

  const handleUpdateObjectType = useCallback(
    async (objectType: NativeObjectType) => {
      const timer = new Date().getTime()

      setSyncState((prev) => {
        if (!prev) return null
        return {
          ...prev,
          [objectType]: {
            ...prev[objectType],
            updatedAt: null,
            objectsNeedingSync: 0,
            needsMoreIds: true,
          },
        }
      })
      const result = await getAllObjectsUpdatedAt({
        variables: { workspaceId, objectType },
      })

      const existingSearchEntriesForObjectType = await getDayObjectsByType({
        workspaceId,
        objectType,
      })

      const syncedObjectIds = new Set<string>(
        existingSearchEntriesForObjectType.map((object) => object.objectId)
      )
      const currentAuthedObjectIds = new Set<string>(
        result.data?.workspaceObjectsUpdatedAt?.map((object) => object.i)
      )

      const objectIdsToDelete = [...syncedObjectIds].filter(
        (objectId) => !currentAuthedObjectIds.has(objectId)
      )

      if (objectIdsToDelete.length > 0) {
        for (const objectId of objectIdsToDelete) {
          await deleteDayObjectSearchEntry(
            buildObjectReference({
              objectType,
              objectId,
              workspaceId,
            })
          )
          await buildFuseIndex(workspaceId)
        }

        logInfo('Deleted objects from search index', {
          objectIdsToDeleteCount: objectIdsToDelete.length,
          sampleObjectIds: objectIdsToDelete.slice(0, 10),
          objectType,
          workspaceId,
        })
      }

      const objectsUpdatedAt = result.data?.workspaceObjectsUpdatedAt
      const timeElapsed = new Date().getTime() - timer

      const searchState = await getDayObjectStateByType({
        workspaceId,
        objectType,
      })

      const searchStateCursor =
        searchState?.[objectType]?.updatedAt || new Date(0)

      const objectIdsNeedingUpdate = objectsUpdatedAt
        ?.filter((object) => {
          if (object.i === '823b350a-da3f-46de-9443-409837b15c5e') {
            logInfo('THIS ONE IS THE PROBLEM', {
              object,
              compareObject: new Date(object.u).getTime(),
              compareState: new Date(searchStateCursor).getTime(),
              searchStateCursor,
              searchState,
            })
          }
          return (
            new Date(object.u).getTime() > new Date(searchStateCursor).getTime()
          )
        })
        .map((object) => object.i)

      logInfo(`Fresh data: ${objectType}`, {
        searchStateCursor,
        objectType,
        objectsUpdatedAtCount: objectsUpdatedAt?.length,
        searchState,
        timeElapsed,
        objectsNeedingUpdateCount: objectIdsNeedingUpdate?.length,
      })

      setSyncState((prev) => {
        if (!prev) return null
        return {
          ...prev,
          [objectType]: {
            ...prev[objectType],
            updatedAt: searchStateCursor,
            objectsNeedingSync: objectIdsNeedingUpdate?.length ?? 0,
            needsMoreIds: false,
          },
        }
      })

      if (objectIdsNeedingUpdate) {
        if (objectIdsNeedingUpdate?.length > 0) {
          await addToSyncQueue({
            objectType,
            items: (objectIdsNeedingUpdate ?? []).map((objectId) => ({
              objectId,
              lastSyncedAt: objectsUpdatedAt?.find(
                (object) => object.i === objectId
              )?.u,
            })),
            workspaceId,
          })
        }
        setSyncState((prev) => {
          if (!prev) return null
          return {
            ...prev,
            [objectType]: {
              ...prev[objectType],
              updatedAt: objectsUpdatedAt?.[0]?.u,
              objectsNeedingSync: 0,
              needsMoreIds: false,
              syncedAt: new Date(),
              processedUntilTimestamp: null,
              currentCount: objectsUpdatedAt?.length ?? 0,
            },
          }
        })
      }

      logInfo('handleUpdateObjectType', {
        message: 'Updated object type',
        objectType,
        timeElapsedMs: timeElapsed,
        searchStateCursor: searchStateCursor?.toISOString(),
        objectIdsNeedingUpdateCount: objectIdsNeedingUpdate?.length ?? 0,
      })

      return {
        objectsUpdatedAt,
        timeElapsed,
        searchState,
        searchStateCursor,
      }
    },
    [getAllObjectsUpdatedAt, workspaceId, logInfo] // logInfo removed - it's now stable
  )

  useEffect(() => {
    if (!workspaceId) return
    logInfo('Mounted (useEffect)', {})

    const handler = async () => {
      const objectTypesToSync = Object.values(NativeObjectTypes).filter(
        (type) => type && ObjectTypeMetadata[type]?.syncEnabled
      )

      for (const objType of objectTypesToSync) {
        await handleUpdateObjectType(objType as NativeObjectType)
      }
    }
    handler()
  }, [handleUpdateObjectType, workspaceId, logInfo])

  const value = useMemo(
    () => ({
      workspaceId,
      propertyDefinitions,
      syncState,
      resetDayObjects: () => {},
      getObjects: () => {},
      updateObject: () => {},
      resyncObject: (objectId, objectType) => {
        handleResyncObjects(objectType, [objectId])
      },
    }),
    [workspaceId, propertyDefinitions, syncState, handleResyncObjects]
  )

  return (
    <ObjectsContext.Provider value={value}>{children}</ObjectsContext.Provider>
  )
}

export default ObjectsProvider
